import React, { useState, useRef, useEffect } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { Button, Progress } from './index'

interface UploadData {
  uploadUrl: string
  fileUrl: string
  fileName: string
}

export interface FileUploadProps {
  onFileUploaded: (fileUrl: string) => void
  onError?: (error: string) => void
  currentImageUrl?: string
  entityType: 'company' | 'group' | 'msb'
  entityId: string
  maxSizeInMB?: number
  onUploadRequest: (file: File) => Promise<UploadData>
  disabled?: boolean
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png']

export function FileUpload({
  onFileUploaded,
  onError,
  currentImageUrl,
  // entityType,
  // entityId,
  // maxSizeInMB = 1,
  onUploadRequest,
  disabled = false,
}: FileUploadProps) {
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null)
  const [imageError, setImageError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setPreviewUrl(currentImageUrl || null)
    setImageError(false)
  }, [currentImageUrl])

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please upload a valid image file (JPEG or PNG only)'
    }

    return null
  }

  const processImageToSquarePNG = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }

          const cropSize = Math.min(img.width, img.height)
          const startX = (img.width - cropSize) / 2
          const startY = (img.height - cropSize) / 2

          const cropCanvas = document.createElement('canvas')
          const cropCtx = cropCanvas.getContext('2d')
          if (!cropCtx) {
            reject(new Error('Failed to get crop canvas context'))
            return
          }

          cropCanvas.width = cropSize
          cropCanvas.height = cropSize
          cropCtx.drawImage(img, startX, startY, cropSize, cropSize, 0, 0, cropSize, cropSize)

          const targetSize = 200
          canvas.width = targetSize
          canvas.height = targetSize
          ctx.drawImage(cropCanvas, 0, 0, cropSize, cropSize, 0, 0, targetSize, targetSize)

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to convert image to PNG'))
                return
              }

              const processedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.png'), {
                type: 'image/png',
                lastModified: Date.now(),
              })

              resolve(processedFile)
            },
            'image/png',
            0.9,
          )
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = URL.createObjectURL(file)
    })
  }

  const handleFile = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      onError?.(validationError)
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      const processedFile = await processImageToSquarePNG(file)
      const objectUrl = URL.createObjectURL(processedFile)
      setPreviewUrl(objectUrl)

      const { uploadUrl, fileUrl } = await onUploadRequest(processedFile)
      await uploadToS3(processedFile, uploadUrl)

      URL.revokeObjectURL(objectUrl)
      setPreviewUrl(fileUrl)
      onFileUploaded(fileUrl)
    } catch (error) {
      console.error('Upload error:', error)
      onError?.(error instanceof Error ? error.message : 'Upload failed')
      setPreviewUrl(currentImageUrl || null)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const uploadToS3 = async (file: File, uploadUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          setUploadProgress(percentComplete)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'))
      })

      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  const removeImage = () => {
    setPreviewUrl(null)
    setImageError(false)
    onFileUploaded('')
  }

  const handleImageError = () => {
    setImageError(true)
  }

  return (
    <div className="space-y-4">
      {!disabled && (
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleChange}
          accept={ACCEPTED_TYPES.join(',')}
        />
      )}

      {previewUrl && !imageError ? (
        <div className="relative">
          <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
            <div className="text-center">
              <img
                src={previewUrl}
                alt="Logo preview"
                className="max-w-32 max-h-32 mx-auto mb-4 rounded-lg object-cover"
                onError={handleImageError}
              />
              <p className="text-sm text-gray-600 mb-4">Current logo</p>
              {!disabled && (
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={openFileDialog} disabled={uploading}>
                    <Upload className="h-4 w-4 mr-2" />
                    Replace
                  </Button>
                  <Button variant="outline" size="sm" onClick={removeImage} disabled={uploading}>
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : disabled ? (
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
          <ImageIcon className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">No company logo yet</p>
          <p className="text-sm text-gray-500">Logo will appear here once uploaded</p>
        </div>
      ) : (
        <div
          className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors ${
            dragActive ? 'border-main/80 bg-main/5' : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'pointer-events-none opacity-50' : ''}`}
          onDragEnter={!disabled ? handleDrag : undefined}
          onDragLeave={!disabled ? handleDrag : undefined}
          onDragOver={!disabled ? handleDrag : undefined}
          onDrop={!disabled ? handleDrop : undefined}
        >
          <ImageIcon className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            Drop your logo here, or{' '}
            <button
              type="button"
              className="text-main/90 hover:text-main underline focus:outline-none cursor-pointer"
              onClick={openFileDialog}
              disabled={uploading || disabled}
            >
              browse
            </button>
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Supports: JPEG, PNG (any size) • Auto-converts to 200x200 PNG
          </p>
        </div>
      )}

      {uploading && (
        <div className="space-y-2">
          <Progress value={uploadProgress} className="w-full" />
          <p className="text-sm text-gray-600 text-center">
            Uploading... {Math.round(uploadProgress)}%
          </p>
        </div>
      )}
    </div>
  )
}
